import { useForm, SubmitHandler, FormProvider } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormElement } from '../FormElement';
import { FormattedMessage, useIntl } from 'react-intl';
import { InputText } from '../Inputs/InputText';
import { InputPostalCode } from '../Inputs/InputPostalCode';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useCreateOrganizationManual,
  getListOrganizationsQueryKey,
  OrganizationStateDTO,
  useGetTenant,
  useListOrganizations,
} from '@/api/trafikportalen';
import { createOrganizationManualBody } from '@/api/trafikportalen/trafikportalenAPI.zod';
import { toast } from '@/hooks/useToast';
import { useQueryClient } from '@tanstack/react-query';
import { useCustomErrorMap } from '@/hooks/useCustomErrorMap';
import { FormSubmitActions } from '../FormSubmitActions';
import { FormSection } from '../FormSection';
import {
  EINVOICE_EMAIL_MAX_LENGTH,
  normalizeGlnInput,
  optionalTrimmedText,
  validateInvoicingIdentifiers,
} from '@/helpers/invoicingIdentifiers';
import { trimData } from '@/helpers/trimData';
import { useTenantConfiguration } from '@/hooks/useTenantConfiguration';
import Input from '../core/Input';
import CheckBox from '../core/Checkbox';
import Button from '../core/Button';
import {
  payerDetailsTranslations,
  possiblePayerDetails,
  validatePayerDetails,
} from '@/helpers/payerDetails';

type OrganizationFormType = z.infer<typeof createOrganizationManualBody>;

interface OrganizationCreateFormProps {
  onOpenChange: (open: boolean) => void;
}

export const OrganizationCreateForm = ({
  onOpenChange,
}: OrganizationCreateFormProps) => {
  const customErrorMap = useCustomErrorMap;
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const { data: tenant } = useGetTenant();
  const { data: organizations } = useListOrganizations();
  const { disableDiscountCodes } = useTenantConfiguration();

  const createOrganization = useCreateOrganizationManual();

  const [creditString, setCreditString] = useState('');
  const [isInternal, setIsInternal] = useState<boolean>(false);
  const [orgNameToCheck, setOrgNameToCheck] = useState<string>('');

  const requiredProps = isInternal
    ? tenant?.required_internal_invoicing_details
    : tenant?.required_external_invoicing_details;

  const optionalProps = isInternal
    ? tenant?.optional_internal_invoicing_details
    : tenant?.optional_external_invoicing_details;

  const allProps = useMemo(
    () => [...(requiredProps || []), ...(optionalProps || [])],
    [requiredProps, optionalProps],
  );

  type RequiredDetails = NonNullable<typeof requiredProps>[number];

  const schema = z
    .preprocess((data) => {
      const values = data as OrganizationFormType;
      return {
        ...values,
        gln: optionalTrimmedText(values.gln),
        einvoice_email: optionalTrimmedText(values.einvoice_email),
      };
    }, createOrganizationManualBody)
    .superRefine((data, ctx) => {
      validatePayerDetails({
        requiredProps,
        optionalProps,
        details: data.payer_details,
        formatMessage,
        fieldPath: 'payer_details',
        ctx,
      });
      validateInvoicingIdentifiers({
        values: data,
        ctx,
        formatMessage,
      });
    });

  const organizationResolver = zodResolver(schema, {
    errorMap: customErrorMap(),
  });

  const methods = useForm<OrganizationFormType>({
    defaultValues: {
      state: OrganizationStateDTO.approved,
      is_internal: false,
      enabled_collection_types: [],
      discount_codes: {},
      credit: -1,
    },
    resolver: organizationResolver,
    mode: 'all',
  });

  const hasNoCreditLimit = methods.watch('credit') === -1;

  const organizationNumber = methods.watch('organization_number');

  useEffect(() => {
    if (!organizationNumber) return;

    if (organizationNumber.includes(' ')) {
      methods.setValue(
        'organization_number',
        organizationNumber.replace(' ', ''),
      );
      return;
    }

    if (organizationNumber.includes('-')) return;

    if (organizationNumber.length > 6) {
      const formatted = `${organizationNumber.slice(0, 6)}-${organizationNumber.slice(6)}`;
      methods.setValue('organization_number', formatted);
    }
  }, [organizationNumber, methods]);

  const duplicateOrgsByName = useMemo(() => {
    if (!orgNameToCheck || !organizations) return [];

    const nameLowerCase = orgNameToCheck.toLowerCase();

    return organizations.filter(
      (org) => org.name.toLowerCase() === nameLowerCase,
    );
  }, [orgNameToCheck, organizations]);

  const hasDuplicateOrganization = useMemo(
    () => duplicateOrgsByName.length > 0,
    [duplicateOrgsByName.length],
  );

  const creditError = useMemo(() => {
    if (!creditString) {
      return '';
    }
    if (isNaN(parseInt(creditString))) {
      return formatMessage({ defaultMessage: 'Ogiltigt värde' });
    }
    return methods.formState.errors.credit?.message;
  }, [creditString, formatMessage, methods.formState.errors.credit?.message]);

  const handleIsInternal = useCallback(() => {
    const internalValue = !methods.getValues('is_internal');
    methods.setValue('is_internal', internalValue);
    methods.reset({
      ...methods.getValues(),
      payer_details: {},
    });
    setIsInternal(!isInternal);
  }, [isInternal, methods]);

  const cancelEdit = useCallback(() => {
    onOpenChange(false);
    methods.reset();
    setOrgNameToCheck('');
  }, [methods, onOpenChange]);

  const onSubmit: SubmitHandler<OrganizationFormType> = useCallback(
    (data) => {
      if (data.credit && data.credit < 0) {
        delete data.credit;
      }

      const master = data.discount_codes?.master;
      if (master && !master.full_product) {
        delete data.discount_codes.master;
      }

      const onlyRelevantDetails = Object.fromEntries(
        Object.entries(data.payer_details).filter(([key]) =>
          allProps.includes(key as RequiredDetails),
        ),
      );

      createOrganization.mutate(
        {
          data: trimData({ ...data, payer_details: onlyRelevantDetails }),
        },
        {
          onSuccess: async () => {
            toast.success(
              {
                title: formatMessage({
                  defaultMessage: 'Organisation skapad.',
                }),
              },
              { autoClose: 3000 },
            );

            await queryClient.invalidateQueries({
              queryKey: getListOrganizationsQueryKey(),
            });

            setOrgNameToCheck('');
            onOpenChange(false);
          },
          onError: (error) => {
            console.error('Error: ', error);
            toast.error(
              {
                title: formatMessage({ defaultMessage: 'Ett fel inträffade' }),
                description: formatMessage({
                  defaultMessage:
                    'Försök spara igen senare, eller vänligen kontakta support.',
                }),
              },
              { autoClose: 3000 },
            );
            return;
          },
        },
      );
    },
    [createOrganization, allProps, formatMessage, queryClient, onOpenChange],
  );

  return (
    <FormProvider {...methods}>
      <form
        className="flex flex-col gap-4"
        onSubmit={methods.handleSubmit(onSubmit)}
      >
        {hasDuplicateOrganization && (
          <div className="rounded-md border border-yellow-500 bg-yellow-50 p-4">
            <div className="flex-1">
              <h3 className="font-semibold text-text-950">
                <FormattedMessage defaultMessage="Varning: Duplicerad organisations­information" />
              </h3>
              <div className="mt-2 space-y-3 text-text-900">
                <p className="font-medium">
                  <FormattedMessage
                    defaultMessage="Det finns redan {count, plural, one {en organisation} other {# organisationer}} med samma namn:"
                    values={{
                      count: duplicateOrgsByName.length,
                    }}
                  />
                </p>
                <ul className="mt-2 list-inside list-disc space-y-1">
                  {duplicateOrgsByName.map((org) => (
                    <li key={org.id}>
                      {org.name} (
                      <FormattedMessage defaultMessage="Org.nr:" />{' '}
                      {org.organization_number})
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        <FormSection
          title={<FormattedMessage defaultMessage="Organisationsinformation" />}
          editMode={false}
        >
          <FormElement
            label={<FormattedMessage defaultMessage="Organisationsnamn" />}
            required={true}
            input={
              <InputText
                name="name"
                control={methods.control}
                editMode={true}
                error={methods.formState.errors.name}
                helperText="Se till att inkludera enhet/avdelning i namnet om relevant"
                helperTextReserveSpace={true}
                onBlur={() => {
                  const currentValue = methods.getValues('name');
                  setOrgNameToCheck(currentValue || '');
                }}
              />
            }
          />
          <FormElement
            label={<FormattedMessage defaultMessage="Organisationsnummer" />}
            required={true}
            input={
              <InputText
                name="organization_number"
                control={methods.control}
                editMode={true}
                error={methods.formState.errors.organization_number}
                helperTextReserveSpace
                placeholder="123456-1234"
              />
            }
          />

          <div className="flex flex-col gap-2">
            <h4 className="text-lg">
              <FormattedMessage defaultMessage="Faktureringsuppgifter" />
            </h4>
            <FormElement
              label={<FormattedMessage defaultMessage="Adress" />}
              required={true}
              input={
                <InputText
                  name="payer_address.street"
                  control={methods.control}
                  editMode={true}
                  error={methods.formState.errors.payer_address?.street}
                  helperTextReserveSpace
                />
              }
            />
            <FormElement
              label={<FormattedMessage defaultMessage="Postnummer" />}
              required={true}
              input={
                <InputPostalCode
                  name="payer_address.zip_code"
                  control={methods.control}
                  editMode={true}
                  error={methods.formState.errors.payer_address?.zip_code}
                  helperTextReserveSpace
                />
              }
            />
            <FormElement
              label={<FormattedMessage defaultMessage="Postort" />}
              required={true}
              input={
                <InputText
                  name="payer_address.city"
                  control={methods.control}
                  editMode={true}
                  error={methods.formState.errors.payer_address?.city}
                  helperTextReserveSpace
                />
              }
            />
            <FormElement
              label={<FormattedMessage defaultMessage="Referens" />}
              required={true}
              input={
                <InputText
                  name="payer_reference"
                  control={methods.control}
                  editMode={true}
                  error={methods.formState.errors.payer_reference}
                  helperTextReserveSpace
                />
              }
            />
            <FormElement
              label={<FormattedMessage defaultMessage="GLN-nummer" />}
              input={
                <InputText
                  name="gln"
                  type="number"
                  normalize={normalizeGlnInput}
                  control={methods.control}
                  editMode={true}
                  error={methods.formState.errors.gln}
                  helperTextReserveSpace
                />
              }
            />
            <FormElement
              label={<FormattedMessage defaultMessage="E-fakturaadress" />}
              input={
                <InputText
                  name="einvoice_email"
                  control={methods.control}
                  editMode={true}
                  error={methods.formState.errors.einvoice_email}
                  helperTextReserveSpace
                  placeholder="faktura@exempel.se"
                  maxLength={EINVOICE_EMAIL_MAX_LENGTH}
                />
              }
            />
          </div>

          <div className="flex flex-col gap-2">
            <h4 className="text-lg">
              <FormattedMessage defaultMessage="Leveransadress" />
            </h4>
            <p className="text-text-700">
              <FormattedMessage defaultMessage="Leveransadressen används för att skicka ut fysiska kort om det beställs." />
            </p>
            <Button
              onClick={() => {
                methods.setValue(
                  'delivery_address.street',
                  methods.watch('payer_address.street'),
                  { shouldDirty: true },
                );
                methods.setValue(
                  'delivery_address.zip_code',
                  methods.watch('payer_address.zip_code'),
                  { shouldDirty: true },
                );
                methods.setValue(
                  'delivery_address.city',
                  methods.watch('payer_address.city'),
                  { shouldDirty: true },
                );
              }}
            >
              <FormattedMessage defaultMessage="Kopiera från faktureringsadress" />
            </Button>
            <FormElement
              label={<FormattedMessage defaultMessage="Adress" />}
              required={true}
              input={
                <InputText
                  name="delivery_address.street"
                  control={methods.control}
                  editMode={true}
                  error={methods.formState.errors.delivery_address?.street}
                  helperTextReserveSpace
                />
              }
            />
            <FormElement
              label={<FormattedMessage defaultMessage="Postnummer" />}
              required={true}
              input={
                <InputPostalCode
                  name="delivery_address.zip_code"
                  control={methods.control}
                  editMode={true}
                  error={methods.formState.errors.delivery_address?.zip_code}
                  helperTextReserveSpace
                />
              }
            />
            <FormElement
              label={<FormattedMessage defaultMessage="Postort" />}
              required={true}
              input={
                <InputText
                  name="delivery_address.city"
                  control={methods.control}
                  editMode={true}
                  error={methods.formState.errors.delivery_address?.city}
                  helperTextReserveSpace
                />
              }
            />
          </div>
        </FormSection>

        <FormSection
          title={<FormattedMessage defaultMessage="Inställningar" />}
          editMode={false}
        >
          {!disableDiscountCodes && (
            <FormElement
              label={<FormattedMessage defaultMessage="Rabattkod" />}
              input={
                <InputText
                  name="discount_codes.master.full_product"
                  control={methods.control}
                  editMode={true}
                />
              }
            />
          )}
          <FormElement
            label={<FormattedMessage defaultMessage="Typ" />}
            input={
              <CheckBox
                id="is_internal"
                label="Intern organisation"
                checked={methods.watch('is_internal')}
                onClick={handleIsInternal}
              />
            }
          />
          <FormElement
            label={<FormattedMessage defaultMessage="Kredit" />}
            required={true}
            input={
              <div className="flex items-center gap-4">
                {!hasNoCreditLimit && (
                  <Input
                    type="text"
                    className="max-w-32"
                    value={creditString}
                    onChange={(e) => {
                      let value = e.target.value;
                      if (value !== '0') {
                        value = value.replace(/^0+/, '');
                      }
                      setCreditString(value);
                      const creditValue = parseInt(value);
                      if (!isNaN(creditValue)) {
                        methods.setValue(
                          'credit',
                          creditValue >= 0 ? creditValue : 0,
                        );
                        methods.trigger();
                      }
                    }}
                    placeholder="0 SEK"
                    error={creditError}
                    onBlur={() =>
                      setCreditString(
                        methods.getValues('credit')?.toString() || '',
                      )
                    }
                  />
                )}
                <CheckBox
                  id="has_no_credit_limit"
                  checked={hasNoCreditLimit}
                  onClick={() => {
                    if (hasNoCreditLimit) {
                      methods.setValue('credit', 0, {
                        shouldDirty: true,
                      });
                    } else {
                      methods.setValue('credit', -1, {
                        shouldDirty: true,
                      });
                    }
                    methods.trigger();
                  }}
                  label={formatMessage({
                    defaultMessage: 'Ingen kreditgräns',
                  })}
                />
              </div>
            }
          />

          {requiredProps && requiredProps.length > 0 && (
            <div className="flex flex-col gap-2">
              <h4 className="text-lg">
                <FormattedMessage defaultMessage="Betalningsuppgifter" />
              </h4>
              {possiblePayerDetails
                .filter((detail) => requiredProps.includes(detail))
                .map((detail) => (
                  <FormElement
                    required
                    key={detail}
                    label={payerDetailsTranslations[detail]}
                    input={
                      <InputText
                        name={`payer_details.${detail}`}
                        control={methods.control}
                        editMode={true}
                        error={methods.formState.errors.payer_details?.[detail]}
                        helperTextReserveSpace
                        type="number"
                      />
                    }
                  />
                ))}
              {possiblePayerDetails.some((detail) =>
                optionalProps?.includes(detail),
              ) && (
                <p className="text-text-800">
                  <FormattedMessage defaultMessage="Valfria faktureringsdetaljer:" />
                </p>
              )}
              {possiblePayerDetails
                .filter((detail) => optionalProps?.includes(detail))
                .map((detail) => (
                  <FormElement
                    key={detail}
                    label={payerDetailsTranslations[detail]}
                    input={
                      <InputText
                        name={`payer_details.${detail}`}
                        control={methods.control}
                        editMode={true}
                        error={methods.formState.errors.payer_details?.[detail]}
                        helperTextReserveSpace
                        type="number"
                      />
                    }
                  />
                ))}
            </div>
          )}
        </FormSection>

        {hasDuplicateOrganization ? (
          <div className="rounded-lg border border-red-500 bg-red-50 p-4">
            <p className="font-bold text-text-950">
              <FormattedMessage defaultMessage="Kan inte skapa organisation" />
            </p>
            <p className="mt-1 text-text-900">
              <FormattedMessage defaultMessage="Det finns redan en organisation med samma namn. Se varningen ovan för detaljer." />
            </p>
          </div>
        ) : (
          <FormSubmitActions
            active={true}
            createMode={true}
            onCancel={cancelEdit}
            isLoading={createOrganization.isPending}
            disabled={hasDuplicateOrganization}
          />
        )}
      </form>
    </FormProvider>
  );
};
